import React, { useCallback } from 'react';
import { match } from 'ts-pattern';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import useNotifications from '../../../hooks/useNotifications';
import { Locale } from '../../Translate';
import Text from '../Text';

import DuplicateIcon from '../../../icons/duplicate2.svg';

import styles from './LinkPreview.scss';

type LinkPreviewProps = {
  children?: string;
  feedbackMessage?: string;
  showCopyButton?: boolean;
  placeholder?: string;
  loadingText?: string;
  isLoading?: boolean;
  isError?: boolean;
  errorText?: string;
};

function LinkPreview(props: LinkPreviewProps) {
  const {
    children,
    feedbackMessage = 'The URL has been copied',
    showCopyButton = true,
    placeholder,
    loadingText,
    isLoading,
    isError,
    errorText,
  } = props;
  const { showNotice } = useNotifications();

  const onButtonClick = useCallback(() => showNotice({ message: feedbackMessage, type: 'info' }), [
    showNotice,
    feedbackMessage,
  ]);

  return (
    <div className={styles.container}>
      {match({
        isError,
        hasResult: !!children,
        isLoading,
        hasLoadingText: !!loadingText,
        hasPlaceholder: !!placeholder,
      })
        // Backwards compatibility special case
        .with(
          {
            isError: false,
            hasLoadingText: false,
            hasPlaceholder: false,
            hasResult: true,
          },
          () => <Text className={styles.link}>{children}</Text>
        )
        // Normal cases
        .with({ isError: true }, () => (
          <Text className={styles['error-text']}>
            <Locale>{errorText || 'An error occurred'}</Locale>
          </Text>
        ))
        .with({ hasResult: true }, () => <Text className={styles.link}>{children}</Text>)
        .with({ isLoading: true }, () => (
          <Text className={styles.link}>
            <Locale>{loadingText}</Locale>
          </Text>
        ))
        .otherwise(() => (
          <Text className={styles.link}>
            <Locale>{placeholder}</Locale>
          </Text>
        ))}
      {showCopyButton && (
        /* @ts-ignore */
        <CopyToClipboard text={children}>
          <button className={styles['copy-button']} aria-label="Copy" onClick={onButtonClick}>
            <DuplicateIcon className={styles.icon} />
          </button>
        </CopyToClipboard>
      )}
    </div>
  );
}

export default React.memo(LinkPreview);
